import { FM_ELEMENT_SCALES } from './elements/elements.types';
import { FM_SECTION_DENSITIES, FM_SECTION_HEADLINES } from './sections/section.types';
import {
  chapterNavLabel,
  FM_REPORT_CHAPTER_TONES,
  type FmReportChapterDescriptor,
  type FmReportChapterSectionRole,
} from './FmReportChapter.types';
import type { FmCopyElement } from './elements/elements.types';
import { plainHeadingText } from './fmHostedHeading';
import { sectionOwnsPrimaryAction } from './sections/sectionContent';
import { validateSection } from './sections/section.validation';
import { assertArchetypePaceCapabilities } from './sections/paceCapabilities';

const ROLE_ORDER: Record<FmReportChapterSectionRole, number> = {
  lead: 0,
  evidence: 1,
  detail: 2,
  action: 3,
};

const FORBIDDEN_LAYOUT_KEYS = [
  'children',
  'className',
  'style',
  'render',
  'layout',
  'mapComponent',
  'chartComponent',
] as const;
const CHAPTER_KEYS = new Set([
  'id', 'navLabel', 'navShortLabel', 'title', 'ownerQuestion', 'literalAnswer', 'dominantBeat',
  'transition', 'mobileOrder', 'conversionRole', 'summary', 'eyebrow', 'tone', 'density',
  'scale', 'headline', 'recipe', 'detailLabel', 'detailOpen', 'detailViews', 'sections', 'testId',
]);
const CONVERSION_ROLES = new Set(['orient', 'build-trust', 'compare', 'decide', 'act', 'retain']);
const RECIPE_KEYS = new Set([
  'requiredServedFields', 'requiredProvenance', 'primaryAction', 'primaryActionSectionId',
  'expertReveal', 'expertSectionIds',
  'stateBehavior', 'assistant', 'confirmation',
]);
const STATE_BEHAVIOR_KEYS = ['loading', 'empty', 'partial', 'stale', 'locked', 'error'] as const;

function assertPlainText(value: unknown, field: string): asserts value is string {
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new Error(`Invalid report chapter: ${field} must be non-empty plain text.`);
  }
}

function assertNoLayoutEscape(value: object, label: string): void {
  FORBIDDEN_LAYOUT_KEYS.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(value, key)) {
      throw new Error(`Invalid report chapter: ${label} cannot provide layout field "${key}".`);
    }
  });
}

function assertTextList(value: unknown, field: string): void {
  if (!Array.isArray(value) || value.length === 0) {
    throw new Error(`Invalid report chapter: ${field} must contain at least one item.`);
  }
  value.forEach((item, index) => assertPlainText(item, `${field}[${index}]`));
  if (new Set(value).size !== value.length) {
    throw new Error(`Invalid report chapter: ${field} must not contain duplicates.`);
  }
}

function validateRecipeRefs(chapter: FmReportChapterDescriptor): void {
  const { recipe } = chapter;
  if (recipe.primaryActionSectionId !== undefined) {
    assertPlainText(recipe.primaryActionSectionId, `chapter "${chapter.id}" recipe.primaryActionSectionId`);
    const actionEntry = chapter.sections.find(({ section }) => section.id === recipe.primaryActionSectionId);
    if (!actionEntry || (actionEntry.role !== 'action' && !sectionOwnsPrimaryAction(actionEntry.section))) {
      throw new Error(`Invalid report chapter: chapter "${chapter.id}" recipe.primaryActionSectionId must reference an action-owning section.`);
    }
  }
  if (recipe.expertSectionIds === undefined) return;
  assertTextList(recipe.expertSectionIds, `chapter "${chapter.id}" recipe.expertSectionIds`);
  const sectionIds = new Set(chapter.sections.map(({ section }) => section.id));
  recipe.expertSectionIds.forEach((sectionId) => {
    if (!sectionIds.has(sectionId)) {
      throw new Error(`Invalid report chapter: chapter "${chapter.id}" recipe.expertSectionIds references unknown section "${sectionId}".`);
    }
  });
}

function validateStateBehavior(chapter: FmReportChapterDescriptor): void {
  const behavior = chapter.recipe.stateBehavior;
  if (!behavior || typeof behavior !== 'object' || Array.isArray(behavior)) {
    throw new Error(`Invalid report chapter: chapter "${chapter.id}" recipe.stateBehavior must be an object.`);
  }
  const behaviorKeys = Object.keys(behavior);
  if (behaviorKeys.length !== STATE_BEHAVIOR_KEYS.length
    || behaviorKeys.some((key) => !STATE_BEHAVIOR_KEYS.includes(key as typeof STATE_BEHAVIOR_KEYS[number]))) {
    throw new Error(`Invalid report chapter: chapter "${chapter.id}" recipe.stateBehavior must define exactly loading, empty, partial, stale, locked, and error.`);
  }
  STATE_BEHAVIOR_KEYS.forEach((state) => {
    assertPlainText(behavior[state], `chapter "${chapter.id}" recipe.stateBehavior.${state}`);
  });
}

function validateAssistant(chapter: FmReportChapterDescriptor): void {
  const assistant = chapter.recipe.assistant;
  if (assistant === undefined) return;
  const allowedKeys = new Set([
    'actionLabel', 'scope', 'propertyId', 'campaignId', 'sourceId', 'revision', 'changeIds',
    'citationIds', 'allowedTools', 'confirmationRequiredFor', 'placement',
  ]);
  const unknownKey = Object.keys(assistant).find((key) => !allowedKeys.has(key));
  if (unknownKey) throw new Error(`Invalid report chapter: chapter "${chapter.id}" recipe.assistant has unknown key "${unknownKey}".`);
  if (!['property', 'campaign'].includes(assistant.scope)) {
    throw new Error(`Invalid report chapter: chapter "${chapter.id}" recipe.assistant.scope must be property or campaign.`);
  }
  assertPlainText(assistant.actionLabel, `chapter "${chapter.id}" recipe.assistant.actionLabel`);
  ['propertyId', 'sourceId', 'revision'].forEach((key) => {
    assertPlainText(assistant[key as 'propertyId' | 'sourceId' | 'revision'], `chapter "${chapter.id}" recipe.assistant.${key}`);
  });
  if (assistant.scope === 'campaign') {
    assertPlainText(assistant.campaignId, `chapter "${chapter.id}" recipe.assistant.campaignId`);
  } else if (assistant.campaignId !== undefined) {
    throw new Error(`Invalid report chapter: chapter "${chapter.id}" property Assistant cannot provide campaignId.`);
  }
  assertTextList(assistant.changeIds, `chapter "${chapter.id}" recipe.assistant.changeIds`);
  assertTextList(assistant.citationIds, `chapter "${chapter.id}" recipe.assistant.citationIds`);
  assertTextList(assistant.confirmationRequiredFor, `chapter "${chapter.id}" recipe.assistant.confirmationRequiredFor`);
  if (assistant.allowedTools.join(',') !== 'explain,navigate') {
    throw new Error(`Invalid report chapter: chapter "${chapter.id}" recipe.assistant.allowedTools must be exactly explain, navigate.`);
  }
  if (assistant.placement !== undefined && assistant.placement !== 'before-sections') {
    throw new Error(`Invalid report chapter: chapter "${chapter.id}" recipe.assistant.placement is invalid.`);
  }
}

function validateConfirmation(chapter: FmReportChapterDescriptor): void {
  const confirmation = chapter.recipe.confirmation;
  if (confirmation === undefined) return;
  const allowed = [
    'actionId', 'transitions', 'proposalRevision', 'humanConfirmationRequired',
  ];
  const unknownKey = Object.keys(confirmation).find((key) => !allowed.includes(key));
  if (unknownKey) throw new Error(`Invalid report chapter: chapter "${chapter.id}" recipe.confirmation has unknown key "${unknownKey}".`);
  ['actionId', 'proposalRevision'].forEach((key) => {
    assertPlainText(
      confirmation[key as 'actionId' | 'proposalRevision'],
      `chapter "${chapter.id}" recipe.confirmation.${key}`,
    );
  });
  if (!Array.isArray(confirmation.transitions) || confirmation.transitions.length === 0) {
    throw new Error(`Invalid report chapter: chapter "${chapter.id}" recipe.confirmation.transitions must contain at least one source transition.`);
  }
  const entityIds = new Set<string>();
  confirmation.transitions.forEach((transition, index) => {
    const path = `chapter "${chapter.id}" recipe.confirmation.transitions[${index}]`;
    if (!transition || typeof transition !== 'object' || Array.isArray(transition)) {
      throw new Error(`Invalid report chapter: ${path} must be an object.`);
    }
    const transitionKeys = Object.keys(transition);
    const unknownTransitionKey = transitionKeys.find(
      (key) => !['entityId', 'prior', 'next'].includes(key),
    );
    if (unknownTransitionKey) {
      throw new Error(`Invalid report chapter: ${path} has unknown key "${unknownTransitionKey}".`);
    }
    assertPlainText(transition.entityId, `${path}.entityId`);
    if (entityIds.has(transition.entityId)) {
      throw new Error(`Invalid report chapter: ${path}.entityId must be unique.`);
    }
    entityIds.add(transition.entityId);
    ([['prior', transition.prior], ['next', transition.next]] as const).forEach(
      ([identityKey, identity]) => {
        const identityPath = `${path}.${identityKey}`;
        if (!identity || typeof identity !== 'object' || Array.isArray(identity)) {
          throw new Error(`Invalid report chapter: ${identityPath} must be an object.`);
        }
        const unknownIdentityKey = Object.keys(identity).find(
          (key) => !['sourceId', 'campaignId'].includes(key),
        );
        if (unknownIdentityKey) {
          throw new Error(`Invalid report chapter: ${identityPath} has unknown key "${unknownIdentityKey}".`);
        }
        assertPlainText(identity.sourceId, `${identityPath}.sourceId`);
        if (identity.campaignId !== null) {
          assertPlainText(identity.campaignId, `${identityPath}.campaignId`);
        }
      },
    );
  });
  if (confirmation.humanConfirmationRequired !== true) {
    throw new Error(`Invalid report chapter: chapter "${chapter.id}" recipe.confirmation must require human confirmation.`);
  }
}

function validateRecipe(chapter: FmReportChapterDescriptor): void {
  const recipe = chapter.recipe;
  if (!recipe || typeof recipe !== 'object' || Array.isArray(recipe)) {
    throw new Error(`Invalid report chapter: chapter "${chapter.id}" recipe must be an object.`);
  }
  const unknownRecipeKey = Object.keys(recipe).find((key) => !RECIPE_KEYS.has(key));
  if (unknownRecipeKey) {
    throw new Error(`Invalid report chapter: chapter "${chapter.id}" recipe has unknown key "${unknownRecipeKey}".`);
  }
  assertTextList(recipe.requiredServedFields, `chapter "${chapter.id}" recipe.requiredServedFields`);
  assertTextList(recipe.requiredProvenance, `chapter "${chapter.id}" recipe.requiredProvenance`);
  assertPlainText(recipe.primaryAction, `chapter "${chapter.id}" recipe.primaryAction`);
  assertPlainText(recipe.expertReveal, `chapter "${chapter.id}" recipe.expertReveal`);
  validateRecipeRefs(chapter);
  validateStateBehavior(chapter);
  validateAssistant(chapter);
  validateConfirmation(chapter);
}

function validateDetailViews(chapter: FmReportChapterDescriptor): void {
  if (chapter.detailViews === undefined) return;
  const detailSections = chapter.sections.filter(({ role }) => role === 'detail');
  if (detailSections.length === 0) {
    throw new Error(`Invalid report chapter: chapter "${chapter.id}" cannot provide detailViews without detail sections.`);
  }
  if (!Array.isArray(chapter.detailViews) || chapter.detailViews.length < 2) {
    throw new Error(`Invalid report chapter: chapter "${chapter.id}" detailViews needs at least two views.`);
  }

  const detailSectionIds = new Set(detailSections.map(({ section }) => section.id));
  const assignedDetailIds = new Set<string>();
  const detailViewIds = new Set<string>();
  chapter.detailViews.forEach((view) => {
    const unknownViewKey = Object.keys(view).find((key) => !['id', 'label', 'sectionIds'].includes(key));
    if (unknownViewKey) throw new Error(`Invalid report chapter: unknown detail view key "${unknownViewKey}".`);
    assertNoLayoutEscape(view, `chapter "${chapter.id}" detail view "${String(view.id)}"`);
    assertPlainText(view.id, `chapter "${chapter.id}" detail view id`);
    assertPlainText(view.label, `chapter "${chapter.id}" detail view "${view.id}" label`);
    if (detailViewIds.has(view.id)) {
      throw new Error(`Invalid report chapter: chapter "${chapter.id}" has duplicate detail view "${view.id}".`);
    }
    detailViewIds.add(view.id);
    if (!Array.isArray(view.sectionIds) || view.sectionIds.length === 0) {
      throw new Error(`Invalid report chapter: chapter "${chapter.id}" detail view "${view.id}" needs at least one section.`);
    }
    view.sectionIds.forEach((sectionId) => {
      assertPlainText(sectionId, `chapter "${chapter.id}" detail view "${view.id}" section id`);
      if (!detailSectionIds.has(sectionId)) {
        throw new Error(`Invalid report chapter: chapter "${chapter.id}" detail view "${view.id}" references non-detail section "${sectionId}".`);
      }
      if (assignedDetailIds.has(sectionId)) {
        throw new Error(`Invalid report chapter: chapter "${chapter.id}" assigns detail section "${sectionId}" more than once.`);
      }
      assignedDetailIds.add(sectionId);
    });
  });
  const unassigned = [...detailSectionIds].filter((sectionId) => !assignedDetailIds.has(sectionId));
  if (unassigned.length > 0) {
    throw new Error(`Invalid report chapter: chapter "${chapter.id}" leaves detail sections unassigned: ${unassigned.join(', ')}.`);
  }
}

function validateSections(chapter: FmReportChapterDescriptor, ids: Set<string>): void {
  let previousRole = -1;
  let switchedSections = 0;
  chapter.sections.forEach((entry) => {
    const unknownEntryKey = Object.keys(entry).find((key) => !['role', 'section'].includes(key));
    if (unknownEntryKey) throw new Error(`Invalid report chapter: unknown section entry key "${unknownEntryKey}".`);
    const { role, section } = entry;
    if (!Object.prototype.hasOwnProperty.call(ROLE_ORDER, role) || ROLE_ORDER[role] < previousRole) {
      throw new Error(`Invalid report chapter: chapter "${chapter.id}" has an invalid section role order.`);
    }
    previousRole = ROLE_ORDER[role];
    assertNoLayoutEscape(section, `section "${String(section.id)}"`);
    validateSection(section);
    assertArchetypePaceCapabilities('FmReportPageTyped', section, `section "${section.id}"`);
    if (section.arrangement === 'tabbed' || section.arrangement === 'carousel') switchedSections += 1;
    if (ids.has(section.id)) throw new Error(`Invalid report chapter: duplicate id "${section.id}".`);
    ids.add(section.id);
  });
  if (switchedSections > 1) {
    throw new Error(`Invalid report chapter: chapter "${chapter.id}" may hold at most one tabbed or carousel section.`);
  }
}

/**
 * One section = one title. The rail label and the eyebrow
 * are the two places a chapter title could be reprinted verbatim — the rail
 * above the section, the eyebrow directly over the heading. They derive from
 * ONE source: an omitted
 * navLabel IS the title, and a navLabel or eyebrow that merely restates the
 * title is a duplicate to delete, not a value to carry.
 */
function assertNoTitleEcho(chapter: FmReportChapterDescriptor): void {
  // Normalized by plainHeadingText, the SAME rule the runtime seal uses to
  // decide a title is a duplicate. A local trim+lowercase here would have let
  // "timber   value" past the validator while FmSectionBand still treated it
  // as an echo and suppressed it — the check and the behaviour disagreeing is
  // worse than either rule alone.
  const title = plainHeadingText(chapter.title);
  if (chapter.navLabel !== undefined) {
    assertPlainText(chapter.navLabel, `chapter "${chapter.id}" navLabel`);
    if (plainHeadingText(chapter.navLabel) === title) {
      throw new Error(
        `Invalid report chapter: chapter "${chapter.id}" navLabel repeats its title. `
        + 'Omit navLabel — the rail derives from the title — or give the rail shorter words.',
      );
    }
  }
  if (chapter.navShortLabel !== undefined) {
    assertPlainText(chapter.navShortLabel, `chapter "${chapter.id}" navShortLabel`);
    if (plainHeadingText(chapter.navShortLabel) === plainHeadingText(chapterNavLabel(chapter))) {
      throw new Error(
        `Invalid report chapter: chapter "${chapter.id}" navShortLabel repeats its rail label. `
        + 'Omit navShortLabel — the narrow rail then shows the full label — or give it shorter words.',
      );
    }
  }
  if (chapter.eyebrow !== undefined && plainHeadingText(chapter.eyebrow) === title) {
    throw new Error(
      `Invalid report chapter: chapter "${chapter.id}" eyebrow repeats its title. `
      + 'A chapter prints ONE title; drop the eyebrow or give it a distinct kicker.',
    );
  }
}

/**
 * The chapter header prints literalAnswer + summary as its description, so a
 * section copy element carrying the same sentence as its lede or a body line
 * prints that sentence twice in a row.
 */
function assertNoSummaryEcho(chapter: FmReportChapterDescriptor): void {
  const printed = new Set(
    [chapter.literalAnswer, chapter.summary]
      .map((text) => plainHeadingText(text))
      .filter((text): text is string => text !== null),
  );
  if (printed.size === 0) return;
  chapter.sections.forEach(({ section }) => {
    copyElementsIn(section).forEach((copy) => {
      [copy.lede, ...(copy.body ?? [])].forEach((line) => {
        const text = plainHeadingText(line);
        if (text !== null && printed.has(text)) {
          throw new Error(
            `Invalid report chapter: section "${section.id}" in chapter "${chapter.id}" repeats the chapter description ("${String(line)}"). `
            + 'The chapter header already prints it; drop it from the section copy.',
          );
        }
      });
    });
  });
}

function isCopyElement(value: object): value is FmCopyElement {
  return 'element' in value && value.element === 'copy';
}

function copyElementsIn(value: unknown, seen = new WeakSet<object>()): FmCopyElement[] {
  if (typeof value !== 'object' || value === null || seen.has(value)) return [];
  seen.add(value);
  if (Array.isArray(value)) return value.flatMap((item) => copyElementsIn(item, seen));
  if (isCopyElement(value)) return [value];
  // Elements never nest elements, so a non-copy element ends the walk and its
  // payload (map geometry, chart rows) is never traversed.
  if ('element' in value) return [];
  return Object.values(value).flatMap((item) => copyElementsIn(item, seen));
}

function validateChapterMetadata(chapter: FmReportChapterDescriptor): void {
  const unknownChapterKey = Object.keys(chapter).find((key) => !CHAPTER_KEYS.has(key));
  if (unknownChapterKey) throw new Error(`Invalid report chapter: unknown chapter key "${unknownChapterKey}".`);
  assertNoLayoutEscape(chapter, `chapter "${String(chapter.id)}"`);
  assertPlainText(chapter.id, 'chapter.id');
  assertPlainText(chapter.title, `chapter "${chapter.id}" title`);
  assertNoTitleEcho(chapter);
  assertPlainText(chapter.ownerQuestion, `chapter "${chapter.id}" ownerQuestion`);
  assertPlainText(chapter.literalAnswer, `chapter "${chapter.id}" literalAnswer`);
  assertPlainText(chapter.dominantBeat, `chapter "${chapter.id}" dominantBeat`);
  assertPlainText(chapter.transition, `chapter "${chapter.id}" transition`);
  if (!CONVERSION_ROLES.has(chapter.conversionRole)) {
    throw new Error(`Invalid report chapter: chapter "${chapter.id}" has invalid conversionRole.`);
  }
  if (chapter.summary !== undefined && typeof chapter.summary !== 'string') {
    throw new Error(`Invalid report chapter: chapter "${chapter.id}" summary must be plain text.`);
  }
  if (chapter.tone !== undefined && !FM_REPORT_CHAPTER_TONES.includes(chapter.tone)) {
    throw new Error(
      `Invalid report chapter: chapter "${chapter.id}" has invalid tone; use ${FM_REPORT_CHAPTER_TONES.join(' | ')}.`,
    );
  }
  if (chapter.density !== undefined && !FM_SECTION_DENSITIES.includes(chapter.density)) {
    throw new Error(`Invalid report chapter: chapter "${chapter.id}" has invalid density.`);
  }
  if (chapter.scale !== undefined && !FM_ELEMENT_SCALES.includes(chapter.scale)) {
    throw new Error(`Invalid report chapter: chapter "${chapter.id}" has invalid scale.`);
  }
  if (chapter.headline !== undefined && !FM_SECTION_HEADLINES.includes(chapter.headline)) {
    throw new Error(`Invalid report chapter: chapter "${chapter.id}" has invalid headline.`);
  }
  validateRecipe(chapter);
}

function validateChapterMobileOrder(
  chapter: FmReportChapterDescriptor,
  sectionIds: readonly string[],
): void {
  if (!Array.isArray(chapter.mobileOrder)
    || chapter.mobileOrder.length !== sectionIds.length
    || new Set(chapter.mobileOrder).size !== sectionIds.length
    || chapter.mobileOrder.some((id) => !sectionIds.includes(id))) {
    throw new Error(`Invalid report chapter: chapter "${chapter.id}" mobileOrder must list every section exactly once.`);
  }
  const rolesById = new Map(chapter.sections.map(({ role, section }) => [section.id, ROLE_ORDER[role]]));
  const mobileRoles = chapter.mobileOrder.map((id) => rolesById.get(id) ?? -1);
  if (mobileRoles.some((role, index) => index > 0 && role < mobileRoles[index - 1])) {
    throw new Error(`Invalid report chapter: chapter "${chapter.id}" mobileOrder must preserve lead/evidence/detail/action role order.`);
  }
}

function validateChapterDetailDisclosure(chapter: FmReportChapterDescriptor): void {
  const hasDetail = chapter.sections.some(({ role }) => role === 'detail');
  if (hasDetail) assertPlainText(chapter.detailLabel, `chapter "${chapter.id}" detailLabel`);
  else if (chapter.detailLabel !== undefined) {
    throw new Error(`Invalid report chapter: chapter "${chapter.id}" cannot provide detailLabel without detail sections.`);
  }
  if (chapter.detailOpen !== undefined && typeof chapter.detailOpen !== 'boolean') {
    throw new Error(`Invalid report chapter: chapter "${chapter.id}" detailOpen must be boolean.`);
  }
  if (!hasDetail && chapter.detailOpen !== undefined) {
    throw new Error(`Invalid report chapter: chapter "${chapter.id}" cannot open missing detail sections.`);
  }
}

function validateChapterShape(chapter: FmReportChapterDescriptor): void {
  if (!Array.isArray(chapter.sections) || chapter.sections.length === 0) {
    throw new Error(`Invalid report chapter: chapter "${chapter.id}" needs at least one section.`);
  }
  if (chapter.sections[0]?.role !== 'lead') {
    throw new Error(`Invalid report chapter: chapter "${chapter.id}" must begin with a lead section.`);
  }
  if (chapter.sections.filter(({ role }) => role !== 'detail').length > 3) {
    throw new Error(`Invalid report chapter: chapter "${chapter.id}" may expose at most three visible sections at rest.`);
  }
  if (chapter.sections.filter(({ role }) => role === 'lead').length !== 1) {
    throw new Error(`Invalid report chapter: chapter "${chapter.id}" needs exactly one lead section.`);
  }
  const sectionIds = chapter.sections.map(({ section }) => section.id);
  if (!sectionIds.includes(chapter.dominantBeat)) {
    throw new Error(`Invalid report chapter: chapter "${chapter.id}" dominantBeat must reference a chapter section.`);
  }
  validateChapterMobileOrder(chapter, sectionIds);
  validateChapterDetailDisclosure(chapter);
  validateDetailViews(chapter);
}

function validateChapter(chapter: FmReportChapterDescriptor, ids: Set<string>): void {
  validateChapterMetadata(chapter);
  if (ids.has(chapter.id)) throw new Error(`Invalid report chapter: duplicate id "${chapter.id}".`);
  ids.add(chapter.id);
  validateChapterShape(chapter);
  validateSections(chapter, ids);
  assertNoSummaryEcho(chapter);
}

export function validateReportChapters(
  chapters: FmReportChapterDescriptor[],
  ids: Set<string> = new Set<string>(),
): void {
  chapters.forEach((chapter) => validateChapter(chapter, ids));
}
